import axios from "axios";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");
const API_URL = `${API_BASE}/api/clientes`;

/* =========================
   Helpers
========================= */
const pickFirst = (...vals) => {
  for (const v of vals) if (v !== undefined && v !== null) return v;
  return undefined;
};

// Inyecta empresaId en los alias comunes
function enrichWithEmpresa(cliente, empresaId) {
  const e = Number.isFinite(+empresaId) ? +empresaId : undefined;
  return {
    ...cliente,
    empresaId: e,
    EmpresaId: e,
    empresa: e,
    Empresa: e,
  };
}

// Sanea payload para la API (ID numérico, nombre→cliente, etc.)
function sanitizeForApi(cliente, empresaId) {
  const out = { ...cliente };

  // ID: si viene "", null o no numérico, lo quitamos para crear
  const idNum = Number(out.ID);
  if (!Number.isFinite(idNum) || idNum <= 0) {
    delete out.ID;
  } else {
    out.ID = idNum;
  }

  // Nombre / Cliente: la API pide "cliente" como requerido
  const nombreVal = pickFirst(
    out.nombre,
    out.Nombre,
    out.NombreFiscal,
    out.razonSocial,
    out.RazonSocial,
    "",
  );

  if (nombreVal) {
    if (!out.nombre) out.nombre = nombreVal;
    if (!out.Nombre) out.Nombre = nombreVal;
    if (!out.cliente) out.cliente = nombreVal;
    if (!out.Cliente) out.Cliente = nombreVal;
  }

  // Normaliza estatus a "A" / "I"
  if (out.estatus === true) out.estatus = "A";
  if (out.estatus === false) out.estatus = "I";

  // Devuelve con empresa inyectada
  return enrichWithEmpresa(out, empresaId);
}

// Normaliza un objeto de la API al shape de tu ClienteForm
export function normalizarCliente(c) {
  if (!c) return null;

  return {
    ID: pickFirst(c.ID, c.id, c.Id, null),
    clave: pickFirst(c.clave, c.Clave, ""),
    rfc: pickFirst(c.rfc, c.RFC, ""),
    nombre: pickFirst(
      c.nombre,
      c.Nombre,
      c.cliente,
      c.Cliente,
      c.razonSocial,
      c.RazonSocial,
      "",
    ),
    NombreFiscal: pickFirst(
      c.NombreFiscal,
      c.nombreFiscal,
      c.Nombre_Fiscal,
      "",
    ),
    direccion: pickFirst(c.direccion, c.Direccion, c.calle, c.Calle, ""),
    colonia: pickFirst(c.colonia, c.Colonia, ""),
    ciudad: pickFirst(c.ciudad, c.Ciudad, c.municipio, c.Municipio, ""),
    estado: pickFirst(c.estado, c.Estado, ""),
    cp: pickFirst(c.cp, c.CP, c.Cp, ""),
    email: pickFirst(c.email, c.Email, ""),
    emailCxP: pickFirst(c.emailCxP, c.EmailCxP, ""),
    estatus: pickFirst(c.estatus, c.Estatus, "A"),
    telefono: pickFirst(c.telefono, c.Telefono, ""),
    delegacion: pickFirst(c.delegacion, c.Delegacion, ""),
    mododepago: pickFirst(c.mododepago, c.modoDePago, c.ModoDePago, ""),
    cuenta_bancaria: pickFirst(
      c.cuenta_bancaria,
      c.cuenta_Bancaria,
      c.CuentaBancaria,
      "",
    ),
    impuestos: pickFirst(c.impuestos, c.Impuestos, false),
    TIPO_CARTERA: pickFirst(c.TIPO_CARTERA, c.tipo_Cartera, c.TipoCartera, 1),
    claveformapago: pickFirst(
      c.claveformapago,
      c.claveFormaPago,
      c.ClaveFormaPago,
      "99",
    ),
    usocfdi: pickFirst(c.usocfdi, c.usoCfdi, c.UsoCfdi, c.UsoCFDI, "P01"),
    metodopago: pickFirst(c.metodopago, c.metodoPago, c.MetodoPago, "PPD"),
    claveformapagoparacomplemento: pickFirst(
      c.claveformapagoparacomplemento,
      c.claveFormaPagoParaComplemento,
      c.ClaveFormaPagoParaComplemento,
      "",
    ),
    Regimen: pickFirst(c.Regimen, c.regimen, ""),
    diasCredito: pickFirst(c.diasCredito, c.DiasCredito, c.diascredito, 0), // ← AGREGAR ESTA LÍNEA
  };
}

const extractOne = (resp) => {
  const d = resp?.data;
  if (!d) return null;
  if (Array.isArray(d?.data)) return d.data[0] ?? null;
  if (Array.isArray(d?.Data)) return d.Data[0] ?? null;
  return d.data ?? d;
};

/* =========================
   CRUD / Listado
========================= */

export const obtenerClientes = (
  empresaId,
  pageNumber = 1,
  pageSize = 10,
  search = "",
  estatus = "",
) =>
  axios.get(`${API_URL}`, {
    params: {
      empresaId,
      pageNumber,
      pageSize,
      search: (search || "").trim(),
      estatus: (estatus || "").trim(),
    },
  });

// Usa sanitizeForApi para enviar lo que la API espera
export const crearCliente = (cliente, empresaId) =>
  axios.post(API_URL, sanitizeForApi(cliente, empresaId));

export const actualizarCliente = (id, cliente, empresaId) =>
  axios.put(`${API_URL}/${id}`, sanitizeForApi(cliente, empresaId));

export const eliminarCliente = (id) => axios.delete(`${API_URL}/${id}`);

/* =========================
   Búsquedas / Detalle
========================= */

export const obtenerClientePorId = (id) => axios.get(`${API_URL}/${id}`);

export async function buscarClientePorClave(empresaId, clave) {
  try {
    const { data } = await axios.get(`${API_URL}/by-clave`, {
      params: { empresaId, clave },
    });
    return data;
  } catch (e) {
    if (e?.response?.status === 404) return null;
    throw e;
  }
}

export async function obtenerClienteDetalle({ id, empresaId, clave }) {
  if (id) {
    try {
      const r = await obtenerClientePorId(id);
      const uno = extractOne(r);
      const norm = normalizarCliente(uno);
      if (norm) return norm;
    } catch {}
  }

  if (empresaId && clave) {
    try {
      const r = await buscarClientePorClave(empresaId, clave);
      const norm = normalizarCliente(r?.data ?? r);
      if (norm) return norm;
    } catch {}
  }

  if (empresaId && clave) {
    try {
      const r = await obtenerClientes(empresaId, 1, 1, clave, "");
      const uno =
        (Array.isArray(r?.data?.Data) && r.data.Data[0]) ||
        (Array.isArray(r?.data?.data) && r.data.data[0]) ||
        null;
      const norm = normalizarCliente(uno);
      if (norm) return norm;
    } catch {}
  }

  return null;
}
